import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { IconDownload } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import i18n from "src/i18n";
import { z } from "zod";

import { CommissionSplitInput } from "@components/ContractManagement/CommissionSplitInput";
import { TierRulesManager } from "@components/ContractManagement/TierRulesManager";
import { CustomPhoneInput } from "@components/CustomPhoneInput";
import { MultiSelectWithSearch } from "@components/MultiSelectWithSearch";
import { SpecialtyMultiInput } from "@components/SpecialtyMultiInput";

import { api, accountDocumentUrl } from "@utils/api";
import { showErrorNotification } from "@utils/showNotification";

import { COUNTRY_CODES } from "@constants/countryCodes";
import { imageFileTypes } from "@constants/fileTypes";

import { useUpdateContract } from "@hooks/mutations/providerContract/useUpdateContract";
import { useGetClinicConsultationServices } from "@hooks/queries/clinic/useGetClinicConsultationServices";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";
import { useGetProfileByUserId } from "@hooks/queries/profile/useGetProfileByUserId";
import { useGetSpecialties } from "@hooks/queries/profile/useGetSpecialties";
import { usePrimarySpecializations, primarySpecializationOptions } from "@hooks/queries/profile/usePrimarySpecializations";
import { useGetContractByProvider } from "@hooks/queries/providerContract/useGetContractByProvider";

import { useAuth } from "@contexts/auth";

import { StyledAddDoctorModalContent } from "../AddDoctorModal/AddDoctorModal.styled";

import { DoctorServiceEditor } from "./DoctorServiceEditor";

// Reuse the same validation schema from AddDoctorModal
const doctorFormSchema = () =>
  z.object({
    doctor: z.string().trim().min(1, i18n.t("doctor-required")),
    email: z.string().trim().email(i18n.t("email-invalid")).or(z.literal("")),
    partnerIds: z.array(z.string()),
    primaryPartnerId: z.string().nullable(),
    isActive: z.boolean(),
    phoneNumber: z
      .string()
      .refine(
        value => !value || value.replace(/\D/g, "").length >= 7,
        i18n.t("invalid-number")
      ),
    primarySpecializationId: z.string().optional(),
    specialties: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        })
      )
      .optional(),
    visibility: z.enum(["public", "private"]),
    services: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        })
      )
      .optional(),
    homecare: z.boolean(),
    teleconsultation: z.boolean(),
    languages: z.array(z.string()).optional(),
    location: z.string().optional(),
    licenseNumber: z.string().optional(),
    licensedCountries: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        })
      )
      .optional(),
    yearsOfExperience: z
      .string()
      .refine(
        value => value === "" || (/^\d+$/.test(value) && Number(value) >= 0),
        i18n.t("invalid-input-title")
      )
      .optional(),
    bio: z.string().max(700).optional(),
    professionalMembership: z.array(z.string()).optional(),
    awardsPublications: z.array(z.string()).optional(),
  });

type DoctorFormInputs = z.infer<ReturnType<typeof doctorFormSchema>>;

interface EditDoctorModalProps {
  opened: boolean;
  close: () => void;
  userId: string | null;
}

// File upload state interface (separate from form data)
interface FileUploads {
  photo?: File;
  medicalLicense?: File;
  proofOfQualification?: File;
  providerContract?: File;
}

export const EditDoctorModal = ({
  opened,
  close,
  userId,
}: EditDoctorModalProps) => {
  const { t } = useTranslation();
  const { token, can } = useAuth();

  const getFileUri = (fileName: string) => accountDocumentUrl(fileName, token);

  const handleDownload = async (fileName: string) => {
    if (!fileName) return;
    try {
      const downloadUrl = getFileUri(fileName);
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName.split("/").pop() || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      const downloadUrl = getFileUri(fileName);
      window.open(downloadUrl, "_blank");
    }
  };

  const [activeTab, setActiveTab] = useState<string | null>("profile");
  const smallScreen = useMediaQuery("(max-width: 48em)");
  const initialProfile = useRef<DoctorFormInputs | null>(null);
  const [editingService, setEditingService] = useState<string | null>(null);
  const [serviceDirty, setServiceDirty] = useState(false);
  const [serviceBusy, setServiceBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string>();
  const queryClient = useQueryClient();
  const { data: partnersData } = useGetPartners({ isEnabled: opened && can("access.manage") });
  const { data: primarySpecializations } = usePrimarySpecializations();
  const [isSaving, setIsSaving] = useState(false);
  const [isPhoneInputError, setIsPhoneInputError] = useState(false);
  const isFormInitialized = useRef(false);
  const isContractInitialized = useRef(false);
  const [isContractModified, setIsContractModified] = useState(false);
  const [contractSaveFailed, setContractSaveFailed] = useState(false);

  // File uploads state (separate from form data)
  const [fileUploads, setFileUploads] = useState<FileUploads>({
    photo: undefined,
    medicalLicense: undefined,
    proofOfQualification: undefined,
    providerContract: undefined,
  });

  // Contract form state (separate from profile form)
  const [contractForm, setContractForm] = useState({
    providerPercentage: 84.0,
    platformPercentage: 16.0,
    tierEnabled: false,
    tierBrackets: [] as {
      min: number;
      max: number | null;
      providerPct: number;
      platformPct: number;
    }[],
    categoryOverrides: {
      teleconsultation: {
        enabled: false,
        providerPct: 84.0,
        platformPct: 16.0,
        reason: "",
      },
      homecare: {
        enabled: false,
        providerPct: 84.0,
        platformPct: 16.0,
        reason: "",
      },
      lab: {
        enabled: false,
        providerPct: 84.0,
        platformPct: 16.0,
        reason: "",
      },
      other: {
        enabled: false,
        providerPct: 84.0,
        platformPct: 16.0,
        reason: "",
      },
    },
  });

  const initialContract = useRef(contractForm);

  // Mantine form with zodResolver
  const form = useForm<DoctorFormInputs>({
    mode: "controlled",
    validate: zodResolver(doctorFormSchema()),
    initialValues: {
      doctor: "",
      email: "",
      partnerIds: [],
      primaryPartnerId: null,
      isActive: true,
      phoneNumber: "",
      primarySpecializationId: "",
      specialties: [],
      visibility: "public",
      services: [],
      homecare: false,
      teleconsultation: false,
      languages: [],
      location: "",
      licenseNumber: "",
      licensedCountries: [],
      yearsOfExperience: "",
      bio: "",
      professionalMembership: [],
      awardsPublications: [],
    },
  });

  // Fetch profile data fresh when editing
  const { data: profileData, isLoading: isLoadingProfile } =
    useGetProfileByUserId({
      userId: userId || "",
      isEnabled: opened && !!userId,
    });

  // Fetch contract data (lazy load when Step 4 is reached)
  // Use partnerId for contract lookup, fallback to userId for individual doctors
  const providerId = profileData?.partnerId || profileData?.userId || "";
  const { data: contractData, isLoading: isLoadingContract } =
    useGetContractByProvider({
      providerId,
      isEnabled: opened && can("access.manage") && !!providerId && activeTab === "credentials",
    });

  // Fetch services data
  const { data: servicesData, isLoading: isLoadingServices } =
    useGetClinicConsultationServices();

  // Fetch specialties data
  const { data: specialtiesData, isLoading: isLoadingSpecialties } =
    useGetSpecialties();

  // Profile update mutation
  const {
    mutate: updateProfile,
    isLoading: isUpdatingProfile,
    error: saveError,
    reset: resetSaveError,
  } = useMutation({
    mutationFn: async ({
      profileId,
      profileData,
    }: {
      profileId: string;
      profileData: FormData;
    }) => api.patch(`profile/${profileId}/admin`, profileData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      queryClient.invalidateQueries({ queryKey: ["clinic"] });
    },
  });

  // Contract update mutation
  const { mutate: updateContract, isLoading: isUpdatingContract } =
    useUpdateContract();

  // Photo upload handling
  const onPhotoDropAccepted = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadError(false);
      setFileUploads(prev => ({ ...prev, photo: acceptedFiles[0] }));
    }
  }, []);

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps } =
    useDropzone({
      accept: { "image/*": imageFileTypes },
      onDrop: onPhotoDropAccepted,
      multiple: false,
      maxSize: 10 * 1024 * 1024,
      onDropRejected: () => setUploadError(true),
    });

  // Compliance documents upload handling
  const onComplianceDropAccepted = useCallback(
    (acceptedFiles: File[], type: keyof FileUploads) => {
      if (acceptedFiles.length > 0) {
        setUploadError(false);
        setFileUploads(prev => ({ ...prev, [type]: acceptedFiles[0] }));
      }
    },
    []
  );

  const {
    getRootProps: getMedicalLicenseRootProps,
    getInputProps: getMedicalLicenseInputProps,
  } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: files => onComplianceDropAccepted(files, "medicalLicense"),
    multiple: false,
    maxSize: 10 * 1024 * 1024,
    onDropRejected: () => setUploadError(true),
  });

  const {
    getRootProps: getProofOfQualRootProps,
    getInputProps: getProofOfQualInputProps,
  } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: files => onComplianceDropAccepted(files, "proofOfQualification"),
    multiple: false,
    maxSize: 10 * 1024 * 1024,
    onDropRejected: () => setUploadError(true),
  });

  const {
    getRootProps: getProviderContractRootProps,
    getInputProps: getProviderContractInputProps,
  } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: files => onComplianceDropAccepted(files, "providerContract"),
    multiple: false,
    maxSize: 10 * 1024 * 1024,
    onDropRejected: () => setUploadError(true),
  });

  const errorTab = (field: string) => {
    if (
      [
        "email",
        "phoneNumber",
        "partnerIds",
        "primaryPartnerId",
        "isActive",
      ].includes(field)
    )
      return "account";
    if (["services", "homecare", "teleconsultation"].includes(field))
      return "services";
    if (field === "licenseNumber") return "credentials";
    return "profile";
  };
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (editingService) return;
    form.onSubmit(
      values => {
        if (isPhoneInputError) {
          form.setFieldError("phoneNumber", t("invalid-number"));
          setActiveTab("account");
          return;
        }
        if (
          values.partnerIds.length &&
          (!values.primaryPartnerId ||
            !values.partnerIds.includes(values.primaryPartnerId))
        ) {
          form.setFieldError(
            "primaryPartnerId",
            t("doctor-editor-primary-required", "Choose a primary partner.")
          );
          setActiveTab("account");
          return;
        }
        if (
          values.isActive !== initialProfile.current?.isActive &&
          !window.confirm(
            t(
              "doctor-editor-account-confirm",
              "Change this doctor's account access? Deactivation blocks authenticated access; public profile visibility is controlled separately."
            )
          )
        )
          return;
        handleSave(values);
      },
      errors => {
        const first = Object.keys(errors)[0];
        setActiveTab(errorTab(first));
        requestAnimationFrame(() => form.getInputNode(first)?.focus());
      }
    )(e);
  };

  // Populate form data when profile data is loaded (only on initial load)
  useEffect(() => {
    if (
      opened &&
      profileData &&
      !isLoadingSpecialties &&
      !isLoadingServices &&
      !isFormInitialized.current
    ) {
      // Map services from API response to form format
      // profileData.services can be either:
      // 1. Array of service objects (current API): [{_id, name, ...}, ...]
      // 2. Array of service IDs (legacy): ["id1", "id2", ...]
      const mappedServices =
        profileData.services
          ?.map(service => {
            if (typeof service === "object" && service !== null) {
              // Current API format: service objects
              return {
                label: (service as any).name,
                value: (service as any)._id,
              };
            } else if (typeof service === "string") {
              // Legacy format: service IDs - find the service name from servicesData
              const serviceData = servicesData?.find(s => s.value === service);
              return serviceData
                ? { label: serviceData.label, value: serviceData.value }
                : { label: service, value: service };
            }
            return null;
          })
          .filter(Boolean) || [];

      // Map specialties from API response to form format
      // profileData.specialties contains IDs, we need to find the corresponding names
      const mappedSpecialties =
        profileData.specialties
          ?.filter(
            specialtyId => specialtyId && typeof specialtyId === "string"
          )
          .map(specialtyId => {
            const specialty = specialtiesData?.find(
              s => s.value === specialtyId
            );
            return specialty
              ? { label: specialty.label, value: specialty.value }
              : { label: specialtyId, value: specialtyId };
          })
          .filter(Boolean) || [];

      // Map licensed countries from API response to form format
      // profileData.licensedCountries contains country codes (e.g., ["US", "CA", "GB"])
      const mappedLicensedCountries =
        profileData.licensedCountries
          ?.map((countryCode: string) => {
            const country = COUNTRY_CODES.find(c => c.value === countryCode);
            return country
              ? { label: country.label, value: country.value }
              : { label: countryCode, value: countryCode };
          })
          .filter(Boolean) || [];

      const initialValues: DoctorFormInputs = {
        doctor: profileData.userInfo?.fullName || "",
        phoneNumber: profileData.userInfo?.phoneNumber || "",
        email: profileData.userInfo?.email || "",
        partnerIds: Array.from(
          new Set([
            ...(profileData.userInfo?.partnerIds || []),
            ...(profileData.partnerId ? [profileData.partnerId] : []),
          ])
        ),
        primaryPartnerId: profileData.partnerId || null,
        isActive: profileData.userInfo?.isActive !== false,
        primarySpecializationId: profileData.primarySpecializationId || "",
        specialties: mappedSpecialties as { label: string; value: string }[],
        visibility: profileData.visibility as "public" | "private",
        services: mappedServices as { label: string; value: string }[],
        homecare: profileData.consultationModes?.includes("homecare") || false,
        teleconsultation:
          profileData.consultationModes?.includes("teleconsultation") || false,
        languages: profileData.languages || [],
        location: profileData.location || "",
        licenseNumber: profileData.licenseNumber || "",
        licensedCountries: mappedLicensedCountries as {
          label: string;
          value: string;
        }[],
        yearsOfExperience: profileData.yearsOfExperience?.toString() || "",
        bio: profileData.briefDescription || "",
        professionalMembership: profileData.professionalMemberships || [],
        awardsPublications: profileData.awardsAndPublications || [],
      };
      form.setValues(initialValues);
      form.resetDirty(initialValues);
      initialProfile.current = initialValues;

      // Mark form as initialized to prevent future resets from cache invalidations
      isFormInitialized.current = true;
    }
  }, [
    opened,
    profileData,
    specialtiesData,
    servicesData,
    isLoadingServices,
    isLoadingSpecialties,
  ]);

  // Populate contract data when contract is loaded (only on initial load)
  useEffect(() => {
    if (contractData && !isContractInitialized.current) {
      // Populate commission split
      setContractForm(prev => ({
        ...prev,
        providerPercentage: contractData.commission_split.provider_percentage,
        platformPercentage: contractData.commission_split.laruche_percentage,
      }));

      // Populate tier rules
      if (
        contractData.tier_rules?.enabled &&
        contractData.tier_rules.brackets
      ) {
        setContractForm(prev => ({
          ...prev,
          tierEnabled: true,
          tierBrackets: contractData.tier_rules!.brackets.map(bracket => ({
            min: bracket.min,
            max: bracket.max,
            providerPct: bracket.provider_pct,
            platformPct: bracket.platform_pct,
          })),
        }));
      }

      // Populate category overrides
      if (contractData.category_overrides) {
        const overrides = contractData.category_overrides;
        setContractForm(prev => ({
          ...prev,
          categoryOverrides: {
            teleconsultation: {
              enabled: !!overrides.teleconsultation,
              providerPct:
                overrides.teleconsultation?.provider_percentage || 84.0,
              platformPct:
                overrides.teleconsultation?.laruche_percentage || 16.0,
              reason: overrides.teleconsultation?.override_reason || "",
            },
            homecare: {
              enabled: !!overrides.homecare,
              providerPct: overrides.homecare?.provider_percentage || 84.0,
              platformPct: overrides.homecare?.laruche_percentage || 16.0,
              reason: overrides.homecare?.override_reason || "",
            },
            lab: {
              enabled: !!overrides.lab,
              providerPct: overrides.lab?.provider_percentage || 84.0,
              platformPct: overrides.lab?.laruche_percentage || 16.0,
              reason: overrides.lab?.override_reason || "",
            },
            other: {
              enabled: !!overrides.other,
              providerPct: overrides.other?.provider_percentage || 84.0,
              platformPct: overrides.other?.laruche_percentage || 16.0,
              reason: overrides.other?.override_reason || "",
            },
          },
        }));
      }

      isContractInitialized.current = true;
    }
  }, [contractData]);

  // Reset form step when modal closes
  useEffect(() => {
    if (!opened) {
      setActiveTab("profile");
      initialProfile.current = null;
      setEditingService(null);
      setServiceDirty(false);
      setFileUploads({});
      setCopied(false);
      resetSaveError();
      setCopyFailed(false);
      setUploadError(false);
      setContractForm(initialContract.current);
      setContractSaveFailed(false);
      isFormInitialized.current = false; // Reset initialization flag for next profile
      isContractInitialized.current = false; // Reset contract initialization flag
      setIsContractModified(false); // Reset contract modification flag
      setIsPhoneInputError(false);
    }
  }, [opened]);

  const handleSave = async (formValues: DoctorFormInputs) => {
    if (!profileData) return;

    setIsSaving(true);

    // Build FormData for multipart/form-data request
    const updateData = new FormData();

    const serialize = (values: DoctorFormInputs) => ({
      doctor: values.doctor,
      email: values.email.trim().toLowerCase(),
      phone_number: values.phoneNumber,
      partner_ids: values.partnerIds,
      partner_id: values.primaryPartnerId,
      is_active: values.isActive,
      visibility: values.visibility,
      ...(values.primarySpecializationId && { primary_specialization_id: values.primarySpecializationId }),
      brief_description: values.bio || "",
      location: values.location || "",
      years_of_experience: !values.yearsOfExperience
        ? 0
        : Number(values.yearsOfExperience),
      license_number: values.licenseNumber || "",
      specialties: (values.specialties || []).map(item => item.label),
      services: (values.services || []).map(item => item.value),
      languages: values.languages || [],
      licensed_countries: (values.licensedCountries || []).map(
        item => item.value
      ),
      professional_memberships: values.professionalMembership || [],
      awards_and_publications: values.awardsPublications || [],
      consultation_modes: [
        values.homecare ? "homecare" : "",
        values.teleconsultation ? "teleconsultation" : "",
      ].filter(Boolean),
    });
    const current = serialize(formValues);
    const initial = serialize(initialProfile.current || formValues);
    const changes = Object.fromEntries(
      Object.entries(current).filter(
        ([key, value]) =>
          JSON.stringify(value) !==
          JSON.stringify(initial[key as keyof typeof initial])
      )
    );
    const partnerChanged =
      formValues.primaryPartnerId !== initialProfile.current?.primaryPartnerId;
    if (
      partnerChanged &&
      (isContractModified || fileUploads.providerContract)
    ) {
      setIsSaving(false);
      showErrorNotification({
        title: "invalid-input-title",
        message: "doctor-editor-partner-contract-separate",
      });
      return;
    }
    updateData.append("changes", JSON.stringify(changes));

    // Profile image (only if new photo selected)
    if (fileUploads.photo) {
      updateData.append("profile_image", fileUploads.photo);
    }
    if (fileUploads.medicalLicense) {
      updateData.append("medical_license", fileUploads.medicalLicense);
    }
    if (fileUploads.proofOfQualification) {
      updateData.append(
        "proof_of_qualification",
        fileUploads.proofOfQualification
      );
    }
    if (fileUploads.providerContract) {
      updateData.append("signed_contract", fileUploads.providerContract);
    }

    // Update profile first
    updateProfile(
      {
        profileId: profileData.id,
        profileData: updateData,
      },
      {
        onSuccess: () => {
          initialProfile.current = formValues;
          form.resetDirty(formValues);
          setFileUploads({});
          // After profile update, update contract only if it was modified
          if (contractData && isContractModified) {
            handleContractSave();
          } else {
            setIsSaving(false);
            close();
          }
        },
        onError: error => {
          console.error("Profile update failed:", error);
          setIsSaving(false);
        },
      }
    );
  };

  const handleContractSave = () => {
    setContractSaveFailed(false);
    if (!contractData) {
      setIsSaving(false);
      close();
      return;
    }

    // Build category overrides object
    const categoryOverrides: Record<string, any> = {};

    if (contractForm.categoryOverrides.teleconsultation.enabled) {
      categoryOverrides.teleconsultation = {
        provider_percentage:
          contractForm.categoryOverrides.teleconsultation.providerPct,
        laruche_percentage:
          contractForm.categoryOverrides.teleconsultation.platformPct,
        override_reason:
          contractForm.categoryOverrides.teleconsultation.reason || null,
      };
    }

    if (contractForm.categoryOverrides.homecare.enabled) {
      categoryOverrides.homecare = {
        provider_percentage:
          contractForm.categoryOverrides.homecare.providerPct,
        laruche_percentage: contractForm.categoryOverrides.homecare.platformPct,
        override_reason: contractForm.categoryOverrides.homecare.reason || null,
      };
    }

    if (contractForm.categoryOverrides.lab.enabled) {
      categoryOverrides.lab = {
        provider_percentage: contractForm.categoryOverrides.lab.providerPct,
        laruche_percentage: contractForm.categoryOverrides.lab.platformPct,
        override_reason: contractForm.categoryOverrides.lab.reason || null,
      };
    }

    if (contractForm.categoryOverrides.other.enabled) {
      categoryOverrides.other = {
        provider_percentage: contractForm.categoryOverrides.other.providerPct,
        laruche_percentage: contractForm.categoryOverrides.other.platformPct,
        override_reason: contractForm.categoryOverrides.other.reason || null,
      };
    }

    // Build contract update payload with required fields
    const contractUpdatePayload = {
      provider: contractData.provider,
      provider_type: contractData.provider_type,
      start_date: contractData.start_date,
      commission_split: {
        provider_percentage: contractForm.providerPercentage,
        laruche_percentage: contractForm.platformPercentage,
      },
      tier_rules: contractForm.tierEnabled
        ? {
            enabled: true,
            reset_period: "monthly" as const,
            brackets: contractForm.tierBrackets.map(bracket => ({
              min: bracket.min,
              max: bracket.max,
              provider_pct: bracket.providerPct,
              platform_pct: bracket.platformPct,
            })),
          }
        : {
            enabled: false,
            reset_period: "monthly" as const,
            brackets: [],
          },
      category_overrides: categoryOverrides,
    };

    updateContract(
      {
        contractId: contractData._id,
        contractData: contractUpdatePayload,
      },
      {
        onSuccess: () => {
          setIsSaving(false);
          close();
        },
        onError: error => {
          console.error("Contract update failed:", error);
          setContractSaveFailed(true);
          setIsSaving(false);
        },
      }
    );
  };

  const hasChanges =
    form.isDirty() ||
    isContractModified ||
    Object.values(fileUploads).some(Boolean);
  const busy =
    isSaving || isUpdatingProfile || isUpdatingContract || serviceBusy;
  const handleClose = () => {
    if (busy) return;
    if (
      (hasChanges || serviceDirty) &&
      !window.confirm(t("doctor-editor-discard", "Discard unsaved changes?"))
    )
      return;
    form.reset();
    close();
  };
  useEffect(() => {
    if (!fileUploads.photo) {
      setPhotoPreview(undefined);
      return;
    }
    const url = URL.createObjectURL(fileUploads.photo);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [fileUploads.photo]);
  useEffect(() => {
    if (!opened || (!hasChanges && !serviceDirty)) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [opened, hasChanges, serviceDirty]);
  const partnerOptions = (partnersData || []).map(partner => ({
    value: partner._id,
    label: partner.name,
  }));
  // Keep existing assignments visible even if their partner was archived or removed.
  form.values.partnerIds.forEach(id => {
    if (!partnerOptions.some(option => option.value === id))
      partnerOptions.push({ value: id, label: id });
  });
  const tabLabels = {
    profile: t("doctor-editor-profile", "Profile"),
    account: t("doctor-editor-account", "Account & Contact"),
    services: t("services"),
    credentials: t("doctor-editor-credentials", "Credentials & Contract"),
  };

  return (
    <Modal
      centered
      size="min(1120px, 96vw)"
      fullScreen={smallScreen}
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      title={<Text fw={600}>{t("edit-profile")}</Text>}
      styles={{
        content: { display: "flex", flexDirection: "column", height: smallScreen ? "100dvh" : "min(900px, 90dvh)", overflow: "hidden" },
        header: { flexShrink: 0 },
        body: { paddingBottom: 0, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, maxHeight: "none", overflow: "hidden" },
      }}>
      {isLoadingProfile ? (
        <Text ta="center" py="xl">
          {t("loading")}
        </Text>
      ) : !profileData ? (
        <Text ta="center" py="xl">
          {t("profile-not-found")}
        </Text>
      ) : (
        <StyledAddDoctorModalContent style={{ flex: 1, minHeight: 0, paddingBottom: 0 }}>
          <Group justify="space-between" align="flex-start" mb="md" style={{ flexShrink: 0 }}>
            <Group>
              <Avatar
                size={64}
                radius="xl"
                src={photoPreview || profileData.profileImage}
              />
              <Stack gap={4}>
                <Text fw={600}>
                  {form.values.doctor || profileData.userInfo?.fullName}
                </Text>
                <Text size="sm" c="dimmed">
                  {primarySpecializationOptions(primarySpecializations).find(item => item.value === form.values.primarySpecializationId)?.label || profileData?.specialization}
                </Text>
                <Group gap="xs">
                  <Badge
                    color={
                      profileData.userInfo?.isActive !== false ? "green" : "red"
                    }>
                    {profileData.userInfo?.isActive !== false
                      ? t("doctor-editor-active", "Account active")
                      : t("doctor-editor-inactive", "Account inactive")}
                  </Badge>
                  <Badge variant="light">
                    {profileData.visibility === "public"
                      ? t("public")
                      : t("private")}
                  </Badge>
                </Group>
              </Stack>
            </Group>
            {profileData?.bookingUrl && (
              <Group gap="xs">
                <Button
                  component="a"
                  href={profileData.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="light"
                  size="xs">
                  {t("doctor-editor-preview", "Preview booking page")}
                </Button>
                <Button
                  size="xs"
                  variant="default"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        profileData.bookingUrl!
                      );
                      setCopied(true);
                      setCopyFailed(false);
                    } catch {
                      setCopied(false);
                      setCopyFailed(true);
                    }
                  }}>
                  {copied
                    ? t("doctor-editor-copied", "Copied")
                    : t("doctor-editor-copy", "Copy booking link")}
                </Button>
              </Group>
            )}
          </Group>
          <Text size="xs" c={copyFailed ? "red" : "dimmed"} role="status" aria-live="polite">
            {copyFailed ? t("editor-copy-failed") : copied ? t("doctor-editor-copied", "Copied") : ""}
          </Text>
          <form onSubmit={handleFormSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <Tabs
              style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
              value={activeTab}
              onChange={value => {
                if (busy) return;
                if (
                  editingService &&
                  serviceDirty &&
                  !window.confirm(
                    t("doctor-editor-discard", "Discard unsaved changes?")
                  )
                )
                  return;
                setEditingService(null);
                setServiceDirty(false);
                setActiveTab(value);
              }}
              keepMounted={false}>
              <Tabs.List style={{ flexWrap: "nowrap", overflowX: "auto", flexShrink: 0 }}>
                {Object.entries(tabLabels).filter(([value]) => value !== "account" || can("access.manage")).map(([value, label]) => (
                  <Tabs.Tab
                    key={value}
                    value={value}
                    c={
                      Object.keys(form.errors).some(
                        field => errorTab(field) === value
                      )
                        ? "red"
                        : undefined
                    }>
                    {label}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  padding: "20px 2px",
                }}>
                <fieldset
                  disabled={busy}
                  style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                  {uploadError && <Alert color="red" mb="md" role="alert">{t("editor-upload-error")}</Alert>}
                  <Tabs.Panel value="profile">
                    <Stack>
                      <Group>
                        <Button variant="light" {...getPhotoRootProps()}>
                          <input {...getPhotoInputProps()} />
                          {t("doctors-photo")}
                        </Button>
                        {fileUploads.photo && (
                          <Group gap="xs">
                            <Text size="sm" style={{ overflowWrap: "anywhere" }}>{fileUploads.photo.name}</Text>
                            <Button variant="subtle" size="xs" onClick={() => setFileUploads(prev => ({ ...prev, photo: undefined }))}>{t("editor-undo-upload")}</Button>
                          </Group>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">{t("editor-photo-help")}</Text>
                      <SimpleGrid cols={{ base: 1, sm: 2 }}>
                        <TextInput
                          label={t("doctor")}
                          withAsterisk
                          {...form.getInputProps("doctor")}
                        />
                        <Select searchable
                          label={t("specialization")}
                          data={primarySpecializationOptions(primarySpecializations)}
                          {...form.getInputProps("primarySpecializationId")}
                        />
                        <TextInput
                          label={t("location")}
                          {...form.getInputProps("location")}
                        />
                        <NumberInput
                          label={t("years-of-experience")}
                          min={0}
                          allowDecimal={false}
                          value={form.values.yearsOfExperience ? Number(form.values.yearsOfExperience) : ""}
                          onChange={value => form.setFieldValue("yearsOfExperience", String(value))}
                          error={form.errors.yearsOfExperience}
                        />
                      </SimpleGrid>
                      <SpecialtyMultiInput
                        label={t("specialties")}
                        placeholder={t("type-a-specialty")}
                        value={form.values.specialties || []}
                        onChange={value =>
                          form.setFieldValue("specialties", value)
                        }
                        error={form.errors.specialties as string}
                      />
                      <TagsInput
                        label={t("languages")}
                        {...form.getInputProps("languages")}
                      />
                      <MultiSelectWithSearch
                        label={t("licensed-countries")}
                        placeholder={t("select-countries")}
                        data={COUNTRY_CODES}
                        value={form.values.licensedCountries || []}
                        setValue={value =>
                          form.setFieldValue(
                            "licensedCountries",
                            typeof value === "function"
                              ? value(form.values.licensedCountries || [])
                              : value
                          )
                        }
                        maxDisplayedValues={999}
                      />
                      <Textarea
                        label={t("bio")}
                        rows={4}
                        description={`${form.values.bio?.length || 0}/700`}
                        maxLength={700}
                        {...form.getInputProps("bio")}
                      />
                      <SimpleGrid cols={{ base: 1, sm: 2 }}>
                        <TagsInput
                          label={t("professional-membership")}
                          {...form.getInputProps("professionalMembership")}
                        />
                        <TagsInput
                          label={t("awards-publications")}
                          {...form.getInputProps("awardsPublications")}
                        />
                      </SimpleGrid>
                      <Radio.Group
                        label={t("visibility")}
                        description={t("editor-visibility-help")}
                        {...form.getInputProps("visibility")}>
                        <Group mt="xs">
                          <Radio value="public" disabled={!can("profiles.publish")} label={t("public")} />
                          <Radio value="private" disabled={!can("profiles.publish")} label={t("private")} />
                        </Group>
                      </Radio.Group>
                    </Stack>
                  </Tabs.Panel>
                  <Tabs.Panel value="account">
                    <Stack>
                      <SimpleGrid cols={{ base: 1, sm: 2 }}>
                        <TextInput
                          label={t("email")}
                          type="email"
                          {...form.getInputProps("email")}
                          description={t(
                            "doctor-editor-email-note",
                            "Used for account communications and password recovery. The login identifier below is unchanged."
                          )}
                        />
                        <TextInput
                          label={t("doctor-editor-login", "Login identifier")}
                          value={profileData.userInfo?.username || ""}
                          readOnly
                        />
                      </SimpleGrid>
                      <div>
                        <Text component="label" htmlFor="edit-doctor-phone-number-input" size="sm" fw={500} mb="xs">
                          {t("phone-number")}
                        </Text>
                        <CustomPhoneInput
                          id="edit-doctor-phone-number"
                          phone={form.values.phoneNumber}
                          setPhone={value =>
                            form.setFieldValue("phoneNumber", value)
                          }
                          setIsError={setIsPhoneInputError}
                        />
                        {form.errors.phoneNumber && (
                          <Text c="red" size="xs">
                            {form.errors.phoneNumber}
                          </Text>
                        )}
                      </div>
                      <MultiSelect
                        label={t(
                          "doctor-editor-partners",
                          "Partner affiliations"
                        )}
                        searchable
                        data={partnerOptions}
                        value={form.values.partnerIds}
                        onChange={values => {
                          form.setFieldValue("partnerIds", values);
                          if (
                            !values.includes(form.values.primaryPartnerId || "")
                          )
                            form.setFieldValue(
                              "primaryPartnerId",
                              values[0] || null
                            );
                        }}
                      />
                      <Select
                        label={t("doctor-editor-primary", "Primary partner")}
                        data={partnerOptions.filter(option =>
                          form.values.partnerIds.includes(option.value)
                        )}
                        disabled={!form.values.partnerIds.length}
                        {...form.getInputProps("primaryPartnerId")}
                      />
                      <Alert color="blue">
                        {t(
                          "doctor-editor-partner-note",
                          "The primary partner determines the profile's clinic affiliation and contract lookup. Existing contracts and appointments are not moved. Save a partner change before editing its contract."
                        )}
                      </Alert>
                      <Paper withBorder p="md">
                        <Text fw={500}>
                          {t("doctor-editor-account-access", "Account access")}
                        </Text>
                        <Text size="sm" c="dimmed" mb="sm">
                          {t(
                            "doctor-editor-access-note",
                            "Disabling the account blocks authenticated access. Use Profile visibility to hide the booking page."
                          )}
                        </Text>
                        <Checkbox
                          label={t("doctor-editor-active", "Account active")}
                          {...form.getInputProps("isActive", {
                            type: "checkbox",
                          })}
                        />
                      </Paper>
                    </Stack>
                  </Tabs.Panel>
                  <Tabs.Panel value="services">
                    <Stack>
                      <MultiSelectWithSearch
                        disabled={!!editingService}
                        label={t("doctor-editor-assign", "Assign services")}
                        placeholder={t("enter-services")}
                        data={servicesData || []}
                        value={form.values.services || []}
                        setValue={value =>
                          form.setFieldValue(
                            "services",
                            typeof value === "function"
                              ? value(form.values.services || [])
                              : value
                          )
                        }
                        maxDisplayedValues={999}
                      />
                      {(form.values.services || []).map(item => {
                        const detail = profileData.services?.find(
                          service =>
                            String(service._id || service.id || service) ===
                            item.value
                        );
                        const persisted =
                          initialProfile.current?.services?.some(
                            service => service.value === item.value
                          );
                        return (
                          <Paper key={item.value} withBorder p="md">
                            <Group justify="space-between">
                              <Stack gap={3}>
                                <Text fw={500}>{item.label}</Text>
                                {detail?.price !== undefined && (
                                  <Text size="sm" c="dimmed">
                                    {Number(detail.price).toLocaleString()} XOF ·{" "}
                                    {t(detail.category || "teleconsultation")} ·{" "}
                                    {detail.service_duration || 30} min ·{" "}
                                    {detail.sessions || 1}{" "}
                                    {t("consultation-service-sessions")}
                                  </Text>
                                )}
                                {!persisted && (
                                  <Text size="xs" c="dimmed">
                                    {t(
                                      "doctor-editor-save-assignment",
                                      "Save this assignment before editing the service."
                                    )}
                                  </Text>
                                )}
                              </Stack>
                              <Group gap="xs">
                                <Button
                                  size="xs"
                                  variant="light"
                                  disabled={!can("profiles.pricing") || !persisted || !!editingService}
                                  onClick={() => setEditingService(item.value)}>
                                  {t("edit")}
                                </Button>
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  color="red"
                                  disabled={!!editingService}
                                  onClick={() =>
                                    form.setFieldValue(
                                      "services",
                                      form.values.services?.filter(
                                        service => service.value !== item.value
                                      )
                                    )
                                  }>
                                  {t("doctor-editor-unassign", "Unassign")}
                                </Button>
                              </Group>
                            </Group>
                            {editingService === item.value && (
                              <PermissionGate permission="profiles.pricing"><DoctorServiceEditor
                                profileId={profileData.id}
                                serviceId={item.value}
                                onBusy={setServiceBusy}
                                onDirty={setServiceDirty}
                                onCancel={() => {
                                  setEditingService(null);
                                  setServiceDirty(false);
                                }}
                                onSaved={(id, name) => {
                                  const replace = (
                                    items: typeof form.values.services
                                  ) =>
                                    items?.map(service =>
                                      service.value === item.value
                                        ? { value: id, label: name }
                                        : service
                                    );
                                  form.setFieldValue(
                                    "services",
                                    replace(form.values.services)
                                  );
                                  if (initialProfile.current)
                                    initialProfile.current.services = replace(
                                      initialProfile.current.services
                                    );
                                  form.resetDirty({
                                    ...initialProfile.current!,
                                  });
                                  setEditingService(null);
                                  setServiceDirty(false);
                                  queryClient.invalidateQueries({
                                    queryKey: ["profiles"],
                                  });
                                  queryClient.invalidateQueries({
                                    queryKey: ["clinic"],
                                  });
                                }}
                              /></PermissionGate>
                            )}
                          </Paper>
                        );
                      })}
                      <Group>
                        <Checkbox
                          label={t("homecare")}
                          {...form.getInputProps("homecare", {
                            type: "checkbox",
                          })}
                        />
                        <Checkbox
                          label={t("teleconsultation")}
                          {...form.getInputProps("teleconsultation", {
                            type: "checkbox",
                          })}
                        />
                      </Group>
                      <Divider />
                      <Text fw={500}>
                        {t("doctor-editor-availability", "Availability")}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {profileData.officeHours ||
                          t(
                            "doctor-editor-schedule-note",
                            "Manage recurring schedules and appointment slots in the scheduler."
                          )}
                      </Text>
                      <Button
                        component="a"
                        href="/appointments/slot-templates"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="light">
                        {t(
                          "doctor-editor-manage-availability",
                          "Manage availability"
                        )}
                      </Button>
                    </Stack>
                  </Tabs.Panel>
                  <Tabs.Panel value="credentials">
                    <Stack>
                      <TextInput
                        label={t("license-number")}
                        {...form.getInputProps("licenseNumber")}
                      />
                      {!can("access.manage") ? null : form.values.primaryPartnerId !==
                      initialProfile.current?.primaryPartnerId ? (
                        <Alert color="yellow">
                          {t(
                            "doctor-editor-partner-contract-separate",
                            "Save partner changes before editing or replacing the contract."
                          )}
                        </Alert>
                      ) : (
                        <>
                          <Stack gap="lg" pos="relative">
                            {/* Loading Overlay for Contract Data */}
                            <LoadingOverlay visible={isLoadingContract} />

                            {contractData ? (
                              <>
                                {/* Contract Status Info */}
                                <Stack gap="xs">
                                  <Group gap="xs">
                                    <Text size="sm" c="dimmed">
                                      {t("contract-status")}:
                                    </Text>
                                    <Text size="sm" fw={500}>
                                      {t("editor-contract-on-file")}
                                    </Text>
                                  </Group>
                                  <Text size="xs" c="dimmed">
                                    {t("start-date")}:{" "}
                                    {new Date(
                                      contractData.start_date
                                    ).toLocaleDateString()}
                                  </Text>
                                </Stack>

                                <Divider />

                                {/* Commission Split */}
                                <CommissionSplitInput
                                  providerPercentage={
                                    contractForm.providerPercentage
                                  }
                                  onProviderPercentageChange={value => {
                                    setContractForm(prev => ({
                                      ...prev,
                                      providerPercentage: value,
                                      platformPercentage: 100 - value,
                                    }));
                                    setIsContractModified(true);
                                  }}
                                  disabled={isSaving || isUpdatingContract}
                                />

                                <Divider />

                                {/* Tier Rules */}
                                <TierRulesManager
                                  enabled={contractForm.tierEnabled}
                                  brackets={contractForm.tierBrackets}
                                  onEnabledChange={enabled => {
                                    setContractForm(prev => ({
                                      ...prev,
                                      tierEnabled: enabled,
                                    }));
                                    setIsContractModified(true);
                                  }}
                                  onBracketsChange={brackets => {
                                    setContractForm(prev => ({
                                      ...prev,
                                      tierBrackets: brackets,
                                    }));
                                    setIsContractModified(true);
                                  }}
                                  disabled={isSaving || isUpdatingContract}
                                />
                              </>
                            ) : (
                              <Text size="sm" c="dimmed" ta="center" py="xl">
                                {t("no-contract-found-for-this-doctor")}
                              </Text>
                            )}
                          </Stack>
                        </>
                      )}
                          <Divider label={t("editor-documents")} />
                          <Stack gap="lg" pos="relative">
                            <Text size="xs" c="dimmed">{t("editor-documents-help")}</Text>
                            <Text size="sm" c="dimmed" mb="md">
                              {t("compliance-documents-description")}
                            </Text>

                            <SimpleGrid cols={{ base: 1, sm: 2 }}>
                              {/* Medical License */}
                              <Stack
                                gap="xs"
                                p="md"
                                style={{
                                  border: "1px solid #eee",
                                  borderRadius: "8px",
                                }}>
                                <Text fw={500}>
                                  {t("onboarding-medical-license")}
                                </Text>
                                {profileData?.userInfo?.medicalLicenseUrl &&
                                !fileUploads.medicalLicense ? (
                                  <Group>
                                    <Button
                                      variant="light"
                                      onClick={() =>
                                        handleDownload(
                                          profileData.userInfo!
                                            .medicalLicenseUrl!
                                        )
                                      }
                                      leftSection={<IconDownload size={16} />}>
                                      {t("download")}
                                    </Button>
                                  </Group>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    {fileUploads.medicalLicense
                                      ? fileUploads.medicalLicense.name
                                      : t("not-provided")}
                                  </Text>
                                )}
                                <Button
                                  variant="outline"
                                  size="xs"
                                  {...getMedicalLicenseRootProps()}>
                                  <input {...getMedicalLicenseInputProps()} />
                                  {t("upload-new-document")}
                                </Button>
                                {fileUploads.medicalLicense && <Button variant="subtle" size="xs" onClick={() => setFileUploads(prev => ({ ...prev, medicalLicense: undefined }))}>{t("editor-undo-upload")}</Button>}
                              </Stack>

                              {/* Proof of Qualification */}
                              <Stack
                                gap="xs"
                                p="md"
                                style={{
                                  border: "1px solid #eee",
                                  borderRadius: "8px",
                                }}>
                                <Text fw={500}>
                                  {t("onboarding-proof-qual")}
                                </Text>
                                {profileData?.userInfo
                                  ?.proofOfQualificationUrl &&
                                !fileUploads.proofOfQualification ? (
                                  <Group>
                                    <Button
                                      variant="light"
                                      onClick={() =>
                                        handleDownload(
                                          profileData.userInfo!
                                            .proofOfQualificationUrl!
                                        )
                                      }
                                      leftSection={<IconDownload size={16} />}>
                                      {t("download")}
                                    </Button>
                                  </Group>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    {fileUploads.proofOfQualification
                                      ? fileUploads.proofOfQualification.name
                                      : t("not-provided")}
                                  </Text>
                                )}
                                <Button
                                  variant="outline"
                                  size="xs"
                                  {...getProofOfQualRootProps()}>
                                  <input {...getProofOfQualInputProps()} />
                                  {t("upload-new-document")}
                                </Button>
                                {fileUploads.proofOfQualification && <Button variant="subtle" size="xs" onClick={() => setFileUploads(prev => ({ ...prev, proofOfQualification: undefined }))}>{t("editor-undo-upload")}</Button>}
                              </Stack>
                            </SimpleGrid>

                            {can("access.manage") && form.values.primaryPartnerId === initialProfile.current?.primaryPartnerId && <Group grow align="flex-start">
                              {/* Provider Contract */}
                              <Stack
                                gap="xs"
                                p="md"
                                style={{
                                  border: "1px solid #eee",
                                  borderRadius: "8px",
                                }}>
                                <Text fw={500}>{t("provider-contract")}</Text>
                                {contractData?.signed_contract_url &&
                                !fileUploads.providerContract ? (
                                  <Group>
                                    <Button
                                      variant="light"
                                      onClick={() =>
                                        handleDownload(
                                          contractData.signed_contract_url!
                                        )
                                      }
                                      leftSection={<IconDownload size={16} />}>
                                      {t("download")}
                                    </Button>
                                  </Group>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    {fileUploads.providerContract
                                      ? fileUploads.providerContract.name
                                      : t("not-provided")}
                                  </Text>
                                )}
                                <Button
                                  variant="outline"
                                  size="xs"
                                  {...getProviderContractRootProps()}>
                                  <input {...getProviderContractInputProps()} />
                                  {t("upload-new-document")}
                                </Button>
                                {fileUploads.providerContract && <Button variant="subtle" size="xs" onClick={() => setFileUploads(prev => ({ ...prev, providerContract: undefined }))}>{t("editor-undo-upload")}</Button>}
                              </Stack>
                            </Group>}
                          </Stack>
                    </Stack>
                  </Tabs.Panel>
                </fieldset>
              </div>
            </Tabs>
            {contractSaveFailed && (
              <Alert color="red" mb="sm">
                {t(
                  "doctor-editor-contract-failed",
                  "The profile was saved, but the contract changes were not. Review the contract and save again."
                )}
              </Alert>
            )}
            {!!saveError && (
              <Alert color="red" mb="sm">
                {typeof (saveError as any)?.response?.data?.detail === "string"
                  ? (saveError as any).response.data.detail
                  : t(
                      "doctor-editor-save-error",
                      "Could not save. Your changes are still here."
                    )}
              </Alert>
            )}
            <Group
              justify="space-between"
              py="md"
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                flexShrink: 0,
                background: "var(--mantine-color-body)",
              }}>
              <Text size="sm" c="dimmed" role="status">
                {editingService ? t("editor-finish-service") : hasChanges || serviceDirty
                  ? t("doctor-editor-unsaved", "Unsaved changes")
                  : t("doctor-editor-no-changes", "No unsaved changes")}
              </Text>
              <Group>
                <Button variant="default" disabled={busy} onClick={handleClose}>
                  {t("cancel")}
                </Button>
                <PermissionGate permission="profiles.edit"><Button
                  type="submit"
                  loading={busy}
                  disabled={!hasChanges || !!editingService}>
                  {t("doctor-editor-save", "Save changes")}
                </Button></PermissionGate>
              </Group>
            </Group>
          </form>
        </StyledAddDoctorModalContent>
      )}
    </Modal>
  );
};
